/**
 * WonderLabs Robot Artist: Explorers (6–7).
 * Independent Journey identity; reuses existing approved media and PWO.
 * Original robotArtistMissionContent.js is intentionally unchanged.
 * Physical results always come from learner reports, never from Swan or PWO.
 */
const EXPLORERS_PAGES = {
  "explorers-robot-prelude": {
    "step_id": "explorers-robot-prelude",
    "title": "Who Drew These?",
    "subtitle": "Four drawings appeared overnight. Nobody saw who made them.",
    "phase": "Prelude",
    "story": "Four drawings appeared overnight. Nobody saw who made them.",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Who Drew These?",
      "say": "Watch the story, look for one clue, and make a first guess.",
      "do_now": "Watch the story, look for one clue, and make a first guess.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next we will open a new clue.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-story-prelude",
        "type": "story_prelude",
        "eyebrow": "CASE FILE 01 · NO PEEKING!",
        "title": "The Mystery of the Robot Artist",
        "prompt": "Watch the mystery story. Keep the artist a secret for now.",
        "video": {
          "title": "The Mystery of the Robot Artist",
          "path": "drawing-robot/prelude/robot_artist_story_v1.mp4",
          "poster": "drawing-robot/prelude/robot_artist_story_poster.jpg",
          "caption": "Look at the drawings. The artist will be revealed in the next phase."
        },
        "reading": {
          "tab_label": "Hear or read the mystery",
          "eyebrow": "CASE FILE 01",
          "title": "Who Drew These?",
          "content": [
            {
              "type": "paragraph",
              "text": "Mira left four blank sheets of paper in the art room. The next morning, every page had strange marks. No one had signed the drawings."
            },
            {
              "type": "paragraph",
              "text": "Some lines curled into loops. Some crossed one another. Some marks looked alike. Who—or what—made them?"
            },
            {
              "type": "question",
              "text": "Look carefully. Which marks can you actually see?"
            }
          ]
        }
      },
      {
        "id": "explorers-first-clue",
        "type": "choice_cards",
        "title": "Spot one clue",
        "prompt": "What did you notice in the four drawings?",
        "save_as": "first_clue",
        "required": true,
        "choices": [
          {
            "id": "loops",
            "label": "Loopy lines"
          },
          {
            "id": "crossings",
            "label": "Lots of crossing lines"
          },
          {
            "id": "repeats",
            "label": "Repeating shapes"
          },
          {
            "id": "different",
            "label": "One drawing looks different"
          }
        ]
      },
      {
        "id": "explorers-first-theory",
        "type": "choice_cards",
        "title": "Your detective idea",
        "prompt": "What do you think might have made the marks? It is okay not to know yet.",
        "save_as": "first_theory",
        "required": true,
        "choices": [
          {
            "id": "person",
            "label": "A person"
          },
          {
            "id": "moving_object",
            "label": "Something moved on the paper"
          },
          {
            "id": "not_sure",
            "label": "I am not sure yet"
          }
        ]
      },
      {
        "id": "explorers-swan-prelude",
        "type": "saras_checkpoint",
        "title": "Ask Professor Swan about a clue",
        "suggested_questions": [
          "Can you help me find one clue?",
          "What is the difference between a clue and a guess?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). The child has NOT seen the robot reveal. Do not name the robot, motor, battery, circuit or any mechanical explanation. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "Protect the artist mystery until Wonder."
      },
      {
        "id": "explorers-prelude-gate",
        "type": "completion_gate",
        "incomplete_title": "Save your detective clues",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Choose one real clue and one first idea.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "first_clue",
            "condition": "answered"
          },
          {
            "field": "first_theory",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "explorers-robot-wonder": {
    "step_id": "explorers-robot-wonder",
    "title": "A Surprising Artist!",
    "subtitle": "A new clue may change your first idea.",
    "phase": "Wonder",
    "story": "A new clue may change your first idea.",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "A Surprising Artist!",
      "say": "Open the surprise clue, then compare it with your very first idea.",
      "do_now": "Open the surprise clue, then compare it with your very first idea.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next we will find the robot parts.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-first-idea-again",
        "type": "prior_work_summary",
        "title": "Your first detective guess",
        "prompt": "Here is what you told us earlier.",
        "items": [
          {
            "source_step_id": "explorers-robot-prelude",
            "field": "first_clue",
            "label": "Clue you noticed",
            "value_labels": {
              "loops": "Loopy lines",
              "crossings": "Crossing lines",
              "repeats": "Repeating shapes",
              "different": "One different drawing"
            }
          },
          {
            "source_step_id": "explorers-robot-prelude",
            "field": "first_theory",
            "label": "Your first idea",
            "value_labels": {
              "person": "A person",
              "moving_object": "Something moved on the paper",
              "not_sure": "Not sure yet"
            }
          }
        ]
      },
      {
        "id": "explorers-robot-reveal",
        "type": "image_reveal",
        "title": "Reveal the mystery artist",
        "prompt": "Tap when you are ready. This is a NEW clue!",
        "image": {
          "path": "drawing-robot/step-2/drawing_robot_reveal.png",
          "alt": "A small drawing robot with colored marker legs above its artwork."
        },
        "save_as": "reveal_viewed",
        "reveal_text": "It is a drawing robot! Your job is to find out HOW it makes its pictures."
      },
      {
        "id": "explorers-surprise",
        "type": "choice_cards",
        "title": "Did that surprise you?",
        "prompt": "What do you think of your first guess now?",
        "save_as": "theory_comparison",
        "required": true,
        "choices": [
          {
            "id": "close",
            "label": "My idea was close"
          },
          {
            "id": "changed",
            "label": "My idea changed"
          },
          {
            "id": "not_sure",
            "label": "I was not sure"
          }
        ]
      },
      {
        "id": "explorers-goal",
        "type": "choice_cards",
        "title": "Choose your artist challenge",
        "prompt": "What would you like your robot to try?",
        "save_as": "mission_goal",
        "required": true,
        "choices": [
          {
            "id": "loops",
            "label": "Make lots of loops"
          },
          {
            "id": "travel",
            "label": "Make wandering lines"
          },
          {
            "id": "discover",
            "label": "Find out what it can draw"
          }
        ]
      },
      {
        "id": "explorers-swan-wonder",
        "type": "saras_checkpoint",
        "title": "Ask Swan about the surprise",
        "suggested_questions": [
          "How could a moving machine make loops?",
          "Can I change my first guess?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). The reveal has happened. Connect the artwork clues to the idea of a moving machine but do NOT teach the whole mechanism now. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "Robot identity is known; do not prematurely teach full motor or wobble mechanism."
      },
      {
        "id": "explorers-wonder-gate",
        "type": "completion_gate",
        "incomplete_title": "Keep investigating",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Open the reveal, think about your first guess, then pick a challenge.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "reveal_viewed",
            "condition": "truthy"
          },
          {
            "field": "theory_comparison",
            "condition": "answered"
          },
          {
            "field": "mission_goal",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "explorers-robot-explore": {
    "step_id": "explorers-robot-explore",
    "title": "Meet Your Robot Parts",
    "subtitle": "These are the real pieces that will make our robot.",
    "phase": "Explore",
    "story": "These are the real pieces that will make our robot.",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Meet Your Robot Parts",
      "say": "Keep the battery out. Find the holder, motor, and three pens.",
      "do_now": "Keep the battery out. Find the holder, motor, and three pens.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next we will find out how power makes things move.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-explore-story",
        "type": "story_hook",
        "eyebrow": "ROBOT ARTIST EXPLORERS",
        "title": "Find the pieces",
        "body": "Open the kit after the mystery reveal. Keep the battery out. Find three colored pens, the motor, and the battery holder. Point to each one.",
        "tone": "mystery"
      },
      {
        "id": "explorers-parts-gallery",
        "type": "image_gallery",
        "title": "Match the parts to your kit",
        "prompt": "These photographs are from your blue-platform robot kit. Find the motor, holder, platform and three pens. The third photo shows what they can become.",
        "images": [
          {
            "id": "parts-real",
            "label": "Our real robot parts",
            "path": "doodle-robot/pwo/references/IMG_4632.jpg",
            "alt": "Actual STEM Discovery Lab drawing robot parts, including blue platform, green foam sleeve, motor, battery holder, metal supports and three pens."
          },
          {
            "id": "parts-guide",
            "label": "The parts spread out",
            "path": "doodle-robot/pwo/references/IMG_4642.jpg",
            "alt": "The actual new drawing robot kit pieces arranged on a dark table so the child can locate each part."
          },
          {
            "id": "virtual-parts",
            "label": "Our built robot",
            "path": "doodle-robot/pwo/references/IMG_4662.jpg",
            "alt": "The finished blue-platform robot from the current kit, with motor, wires and three drawing pens."
          }
        ]
      },
      {
        "id": "explorers-parts-threejs",
        "type": "pwo_asset",
        "title": "Look at the robot in 3D",
        "mission": "Start at Meet the Parts. Rotate the 3D guide and compare the model with your actual kit. This is the virtual model, not proof of your physical build.",
        "save_as": "parts_3d_opened",
        "asset": {
          "id": "explorers-robot-parts-v1",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": "wl.doodle_robot.assembly",
            "scene_name": "mainmenu"
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1
          }
        }
      },
      {
        "id": "explorers-found-three",
        "type": "choice_cards",
        "title": "Did you find the real parts?",
        "prompt": "Point to the motor, the battery holder and all three pens. Ask Professor Swan if you need a clue.",
        "save_as": "parts_checked",
        "required": true,
        "choices": [
          {
            "id": "found",
            "label": "Yes, we found them"
          },
          {
            "id": "helped",
            "label": "Yes, I found them"
          }
        ]
      },
      {
        "id": "explorers-job-prediction",
        "type": "choice_cards",
        "title": "Choose a job",
        "prompt": "What do you think the motor does?",
        "save_as": "part_job_prediction",
        "required": true,
        "choices": [
          {
            "id": "spin",
            "label": "Makes something spin"
          },
          {
            "id": "hold",
            "label": "Holds the battery"
          },
          {
            "id": "draw",
            "label": "Leaves colored marks"
          }
        ]
      },
      {
        "id": "explorers-swan-parts",
        "type": "saras_checkpoint",
        "title": "Ask about a real part",
        "suggested_questions": [
          "Which piece is the motor?",
          "I cannot find the battery holder."
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). The child must locate actual pieces. The 3D screenshot only shows a model. Battery is out; no powered test yet. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "You may identify visible virtual parts but never claim the child owns or has found one."
      },
      {
        "id": "explorers-parts-gate",
        "type": "completion_gate",
        "incomplete_title": "Meet the three important parts",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Find the real pieces with help and make one job prediction.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "parts_checked",
            "condition": "answered"
          },
          {
            "field": "part_job_prediction",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "explorers-robot-understand": {
    "step_id": "explorers-robot-understand",
    "title": "What Makes It Wiggle?",
    "subtitle": "Electricity helps the motor spin. The robot wobbles because the crank is off-center.",
    "phase": "Understand",
    "story": "Electricity helps the motor spin. The robot wobbles because the crank is off-center.",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "What Makes It Wiggle?",
      "say": "Make a prediction and put three picture ideas in the order you think makes sense.",
      "do_now": "Make a prediction and put three picture ideas in the order you think makes sense.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next the real robot gets built.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-understand-story",
        "type": "story_hook",
        "eyebrow": "ROBOT ARTIST EXPLORERS",
        "title": "From power to wiggle",
        "body": "Imagine a battery, a switch and a tiny motor working together. What must happen before the motor can spin?",
        "tone": "mystery"
      },
      {
        "id": "explorers-circuit-gallery",
        "type": "image_gallery",
        "title": "Look at the path",
        "prompt": "Find the picture showing a complete path. No one needs to connect the real wires yet.",
        "images": [
          {
            "id": "circuit-path",
            "label": "Open or closed?",
            "path": "drawing-robot/step-5/closed_vs_open_circuit.png",
            "alt": "Diagram comparing open and closed electrical paths."
          },
          {
            "id": "wobble",
            "label": "Off-center motion",
            "path": "drawing-robot/step-6/eccentric_vibration.png",
            "alt": "Diagram of a motor and off-center part causing wobble."
          }
        ]
      },
      {
        "id": "explorers-switch-guess",
        "type": "choice_cards",
        "title": "Which switch position will help it spin?",
        "prompt": "Predict before testing.",
        "save_as": "switch_prediction",
        "required": true,
        "choices": [
          {
            "id": "closed",
            "label": "A closed connection"
          },
          {
            "id": "open",
            "label": "A broken connection"
          },
          {
            "id": "unsure",
            "label": "I am not sure"
          }
        ]
      },
      {
        "id": "explorers-wobble-guess",
        "type": "choice_cards",
        "title": "What will happen when the little crank spins?",
        "prompt": "Make a prediction about a weight that is not in the center.",
        "save_as": "wobble_prediction",
        "required": true,
        "choices": [
          {
            "id": "wiggle",
            "label": "The body may wiggle"
          },
          {
            "id": "steady",
            "label": "The body will stay perfectly still"
          },
          {
            "id": "unsure",
            "label": "I am not sure"
          }
        ]
      },
      {
        "id": "explorers-motion-sequence",
        "type": "sequence_builder",
        "title": "What happens first?",
        "prompt": "Put the picture ideas in order. You can change your mind.",
        "save_as": "motion_chain",
        "shuffle_items": true,
        "items": [
          {
            "id": "battery",
            "label": "Battery and connected switch ⚡"
          },
          {
            "id": "motor",
            "label": "Motor starts spinning 🔄"
          },
          {
            "id": "wiggle",
            "label": "Robot body wiggles 🤖"
          }
        ],
        "correct_order": [
          "battery",
          "motor",
          "wiggle"
        ],
        "correct_feedback": "You found a way that power can become motion.",
        "retry_feedback": "Think about what must happen before the motor can spin."
      },
      {
        "id": "explorers-swan-understand",
        "type": "saras_checkpoint",
        "title": "Ask Swan about movement",
        "suggested_questions": [
          "Why does a motor need a connected path?",
          "What does an off-center crank do?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). Guide through battery→closed path→spinning motor→off-center crank→wobble, with short concrete explanations and no assumption that the real circuit has been built. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "Simple mechanism explanation is appropriate; no claims about physical results."
      },
      {
        "id": "explorers-understand-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish your power detective work",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Save two predictions and put three events in order.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "switch_prediction",
            "condition": "answered"
          },
          {
            "field": "wobble_prediction",
            "condition": "answered"
          },
          {
            "field": "motion_chain",
            "condition": "sequence_correct"
          }
        ]
      }
    ]
  },
  "explorers-robot-apply": {
    "step_id": "explorers-robot-apply",
    "title": "Build and Try Your Robot",
    "subtitle": "Build the real Doodle Robot, then watch what happens on the first safe test.",
    "phase": "Apply",
    "story": "Build the real Doodle Robot, then watch what happens on the first safe test.",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Build and Try Your Robot",
      "say": "Build with the 3D guide. Make a prediction before turning it on.",
      "do_now": "Build with the 3D guide. Make a prediction before turning it on.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next you can try one change.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-apply-safety",
        "type": "story_hook",
        "eyebrow": "ROBOT ARTIST EXPLORERS",
        "title": "Build the tricky bits step by step",
        "body": "Keep the battery out during assembly. Check screws and wire connections against the guide with power off. Then test on a large paper-covered surface.",
        "tone": "mystery"
      },
      {
        "id": "explorers-assembly-pwo",
        "type": "pwo_asset",
        "title": "Open your 3D build guide",
        "mission": "Follow the approved ten-step animated assembly, beginning with Meet the Parts. Keep power off while fastening screws and connecting wires. Use the printed manual too.",
        "save_as": "assembly_3d_opened",
        "asset": {
          "id": "explorers-doodle-robot-assembly-v1",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": "wl.doodle_robot.assembly",
            "scene_name": "mainmenu"
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1
          }
        }
      },
      {
        "id": "explorers-build-check",
        "type": "build_checklist",
        "title": "Check the real build",
        "prompt": "Choose Completed only when the real-world check is done. I need help opens Swan.",
        "save_as": "build_checklist",
        "items": [
          {
            "id": "supports",
            "label": "Pen supports are fixed and all three pens touch the paper",
            "help": "Compare the real parts to the approved 3D steps. Tell Swan if a screw does not fit."
          },
          {
            "id": "motor",
            "label": "Motor and off-center crank are secure",
            "help": "Keep battery out. Check the crank has room to turn freely."
          },
          {
            "id": "wires",
            "label": "I checked the wire and switch connections with power off",
            "help": "Follow the supplied instructions; never power the circuit while changing wire connections."
          },
          {
            "id": "test_area",
            "label": "Paper is ready and the space is clear",
            "help": "Choose a stable protected surface with no loose objects near moving parts."
          }
        ]
      },
      {
        "id": "explorers-first-prediction",
        "type": "choice_cards",
        "title": "What do you predict?",
        "prompt": "Before the first test, what might happen?",
        "save_as": "test1_prediction",
        "required": true,
        "choices": [
          {
            "id": "loops",
            "label": "It may draw loops"
          },
          {
            "id": "wander",
            "label": "It may wander across the page"
          },
          {
            "id": "little",
            "label": "It may hardly move"
          },
          {
            "id": "unsure",
            "label": "I am not sure"
          }
        ]
      },
      {
        "id": "explorers-first-test",
        "type": "experiment_record",
        "title": "What really happened?",
        "prompt": "Put a large sheet of paper under the robot. Uncap all three pens and check that each tip touches the paper. After your prediction, press the switch to start the motor. Watch the robot wiggle and draw, then switch it off. Do not touch moving parts. It is okay if it did not work.",
        "save_as": "test1_record",
        "fields": [
          {
            "id": "motion",
            "type": "choice",
            "label": "Did the robot move?",
            "choices": [
              {
                "id": "yes",
                "label": "Yes, it moved"
              },
              {
                "id": "little",
                "label": "A little"
              },
              {
                "id": "no",
                "label": "No, it stayed still"
              }
            ]
          },
          {
            "id": "drawing",
            "type": "choice",
            "label": "What marks appeared?",
            "choices": [
              {
                "id": "loops",
                "label": "Loops or circles"
              },
              {
                "id": "wanders",
                "label": "Long wandering lines"
              },
              {
                "id": "few",
                "label": "Very few marks"
              },
              {
                "id": "none",
                "label": "No marks"
              }
            ]
          }
        ]
      },
      {
        "id": "explorers-swan-build",
        "type": "saras_checkpoint",
        "title": "Ask Swan about your build",
        "suggested_questions": [
          "I need help with the assembly.",
          "My robot is not drawing. What should I check first?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). Child physically builds the robot and reports first test. 3D is a virtual guide only; question about motor, wires or pens should receive safe, gradual troubleshooting. Swan guides age-appropriate battery and terminal checks with power off. Do not claim a test was done until child records it. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "The mechanism can be taught; actual performance must come from child report."
      },
      {
        "id": "explorers-apply-gate",
        "type": "completion_gate",
        "incomplete_title": "Save your first real test",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Finish your build, predict, and record what actually happened.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "build_checklist",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "test1_prediction",
            "condition": "answered"
          },
          {
            "field": "test1_record",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "explorers-robot-test-improve": {
    "step_id": "explorers-robot-test-improve",
    "title": "One Change, Another Drawing",
    "subtitle": "Scientists try one change and look for a difference.",
    "phase": "Test & Improve",
    "story": "Scientists try one change and look for a difference.",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "One Change, Another Drawing",
      "say": "Turn power off first. Pick one approved change, then compare a second real test.",
      "do_now": "Turn power off first. Pick one approved change, then compare a second real test.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next you will explain the robot with your evidence.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-first-test-summary",
        "type": "prior_work_summary",
        "title": "Your first drawing",
        "prompt": "Here is what you told us earlier.",
        "items": [
          {
            "source_step_id": "explorers-robot-apply",
            "field": "test1_prediction",
            "label": "Your first prediction",
            "value_labels": {
              "loops": "Loops",
              "wander": "Wandering",
              "little": "Little movement",
              "unsure": "Not sure"
            }
          },
          {
            "source_step_id": "explorers-robot-apply",
            "field": "test1_record",
            "label": "What your robot actually did in Test 1",
            "field_labels": {
              "motion": "Motion",
              "drawing": "Drawing"
            },
            "value_labels": {
              "yes": "Moved",
              "little": "Moved a little",
              "no": "Stayed still",
              "loops": "Loops",
              "wanders": "Wandering lines",
              "few": "Few marks",
              "none": "No marks"
            }
          }
        ]
      },
      {
        "id": "explorers-one-change",
        "type": "choice_cards",
        "title": "Choose one safe change",
        "prompt": "With power off, choose ONE thing to change.",
        "save_as": "change_selected",
        "required": true,
        "choices": [
          {
            "id": "pen_contact",
            "label": "Adjust pen contact with power off"
          },
          {
            "id": "paper",
            "label": "Try another suitable paper surface"
          },
          {
            "id": "no_change",
            "label": "Repeat without changing anything"
          }
        ]
      },
      {
        "id": "explorers-second-prediction",
        "type": "choice_cards",
        "title": "What do you think will happen?",
        "prompt": "Will your new drawing look different?",
        "save_as": "test2_prediction",
        "required": true,
        "choices": [
          {
            "id": "different",
            "label": "Different marks"
          },
          {
            "id": "similar",
            "label": "Similar marks"
          },
          {
            "id": "unsure",
            "label": "I am not sure"
          }
        ]
      },
      {
        "id": "explorers-second-test",
        "type": "experiment_record",
        "title": "What happened the second time?",
        "prompt": "Select what you REALLY observed in the second attempt. If you did not test safely, choose that. Do not guess.",
        "save_as": "test2_record",
        "fields": [
          {
            "id": "result",
            "type": "choice",
            "label": "What happened after your change?",
            "choices": [
              {
                "id": "more_loops",
                "label": "I saw more loops"
              },
              {
                "id": "fewer_loops",
                "label": "I saw fewer loops"
              },
              {
                "id": "different",
                "label": "I saw different lines"
              },
              {
                "id": "similar",
                "label": "It looked much the same"
              },
              {
                "id": "no_marks",
                "label": "It made no marks"
              },
              {
                "id": "unsafe",
                "label": "It was not safe to run a second test"
              },
              {
                "id": "not_possible",
                "label": "We could not test today"
              }
            ]
          }
        ]
      },
      {
        "id": "explorers-drawing-comparison",
        "type": "choice_cards",
        "title": "Compare the pictures",
        "prompt": "Which statement fits your two tests? Even a test that did not work gives evidence.",
        "save_as": "comparison",
        "required": true,
        "choices": [
          {
            "id": "changed",
            "label": "The drawings look different"
          },
          {
            "id": "same",
            "label": "They look quite similar"
          },
          {
            "id": "no_second",
            "label": "We could not compare safely"
          }
        ]
      },
      {
        "id": "explorers-swan-compare",
        "type": "saras_checkpoint",
        "title": "Ask Swan about your two drawings",
        "suggested_questions": [
          "How do I compare my two drawings?",
          "My second test did not work. What did I learn?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). Use actual test1_record from Apply and test2_record from this phase. Do not claim improvement or prettier art. If unsafe or impossible, encourage an honest report; do not push the child into performing an unsafe test. Only one change and power off for adjustments. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "The two trials may differ or not; do not invent comparative outcomes."
      },
      {
        "id": "explorers-test-improve-gate",
        "type": "completion_gate",
        "incomplete_title": "Save your second investigation",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Record the chosen change, prediction, observed or safely unavailable second result, and honest comparison.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "change_selected",
            "condition": "answered"
          },
          {
            "field": "test2_prediction",
            "condition": "answered"
          },
          {
            "field": "test2_record",
            "condition": "record_complete"
          },
          {
            "field": "comparison",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "explorers-robot-explain": {
    "step_id": "explorers-robot-explain",
    "title": "How Did Our Robot Draw?",
    "subtitle": "What happened between the battery and the colored marks?",
    "phase": "Explain",
    "story": "What happened between the battery and the colored marks?",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "How Did Our Robot Draw?",
      "say": "Follow the real parts and tell a short cause-and-effect story.",
      "do_now": "Follow the real parts and tell a short cause-and-effect story.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next you can compare your first guess with your discovery.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-explain-gallery",
        "type": "image_gallery",
        "title": "See the system",
        "prompt": "Use the picture to remember how the parts cooperate.",
        "images": [
          {
            "id": "system",
            "label": "The whole robot system",
            "path": "drawing-robot/step-9/system_synthesis.png",
            "alt": "Schematic of the drawing robot as an electric and mechanical system."
          }
        ]
      },
      {
        "id": "explorers-explain-chain",
        "type": "sequence_builder",
        "title": "Tell the machine story",
        "prompt": "Put the events in the order that makes the artwork.",
        "save_as": "cause_chain",
        "shuffle_items": true,
        "items": [
          {
            "id": "power",
            "label": "Battery gives the motor power ⚡"
          },
          {
            "id": "spin",
            "label": "Motor turns the crank 🔄"
          },
          {
            "id": "wobble",
            "label": "Robot wiggles 🤖"
          },
          {
            "id": "marks",
            "label": "Pens make marks 🎨"
          }
        ],
        "correct_order": [
          "power",
          "spin",
          "wobble",
          "marks"
        ],
        "correct_feedback": "That sequence connects power, motion and art.",
        "retry_feedback": "Which event needs to happen before the robot can wiggle?"
      },
      {
        "id": "explorers-evidence-point",
        "type": "choice_cards",
        "title": "Point to your evidence",
        "prompt": "What did your REAL test show?",
        "save_as": "evidence_point",
        "required": true,
        "choices": [
          {
            "id": "moved",
            "label": "My robot moved"
          },
          {
            "id": "mark",
            "label": "I saw marks on the paper"
          },
          {
            "id": "none",
            "label": "It did not work yet"
          },
          {
            "id": "compared",
            "label": "I compared two drawings"
          }
        ]
      },
      {
        "id": "explorers-swan-explain",
        "type": "saras_checkpoint",
        "title": "Tell Swan what you figured out",
        "suggested_questions": [
          "Help me explain how the battery and motor work together.",
          "How can I explain a test that did not work?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). Let the child say their own causal explanation. Child may have recorded no movement: respect that; distinguish how the intended mechanism works from what their own test demonstrated. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "All core mechanisms may be discussed after actual testing."
      },
      {
        "id": "explorers-explain-gate",
        "type": "completion_gate",
        "incomplete_title": "Tell your science story",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Order the parts and cite one thing you really observed.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "cause_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "evidence_point",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "explorers-robot-reflect": {
    "step_id": "explorers-robot-reflect",
    "title": "My First Guess, My Discovery",
    "subtitle": "Look at how your thinking changed when you collected clues and tested.",
    "phase": "Reflect",
    "story": "Look at how your thinking changed when you collected clues and tested.",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "My First Guess, My Discovery",
      "say": "Remember your first guess. What do you know now?",
      "do_now": "Remember your first guess. What do you know now?",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "Next we will open your gallery.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-original-clue",
        "type": "prior_work_summary",
        "title": "Your first case-file clue",
        "prompt": "Here is what you told us earlier.",
        "items": [
          {
            "source_step_id": "explorers-robot-prelude",
            "field": "first_clue",
            "label": "You noticed",
            "value_labels": {
              "loops": "Loopy lines",
              "crossings": "Crossing lines",
              "repeats": "Repeating shapes",
              "different": "One different drawing"
            }
          },
          {
            "source_step_id": "explorers-robot-prelude",
            "field": "first_theory",
            "label": "You thought",
            "value_labels": {
              "person": "A person",
              "moving_object": "Something moved on the paper",
              "not_sure": "Not sure yet"
            }
          }
        ]
      },
      {
        "id": "explorers-test-summary",
        "type": "prior_work_summary",
        "title": "Your second test",
        "prompt": "Here is what you told us earlier.",
        "items": [
          {
            "source_step_id": "explorers-robot-test-improve",
            "field": "comparison",
            "label": "Your comparison",
            "value_labels": {
              "changed": "Drawings were different",
              "same": "Drawings looked similar",
              "no_second": "Could not compare safely"
            }
          },
          {
            "source_step_id": "explorers-robot-test-improve",
            "field": "test2_record",
            "label": "What you actually observed in Test 2",
            "field_labels": {
              "result": "Second result"
            },
            "value_labels": {
              "more_loops": "More loops",
              "fewer_loops": "Fewer loops",
              "different": "Different lines",
              "similar": "Similar marks",
              "no_marks": "No marks",
              "unsafe": "Not safe to test",
              "not_possible": "Could not test today"
            }
          }
        ]
      },
      {
        "id": "explorers-reflection-choice",
        "type": "choice_cards",
        "title": "Look back at your guess",
        "prompt": "What do you think now?",
        "save_as": "reflection_choice",
        "required": true,
        "choices": [
          {
            "id": "changed",
            "label": "My idea changed"
          },
          {
            "id": "close",
            "label": "My idea was close"
          },
          {
            "id": "questions",
            "label": "I still have a question"
          }
        ]
      },
      {
        "id": "explorers-reflection-evidence",
        "type": "choice_cards",
        "title": "What helped you learn?",
        "prompt": "Choose the evidence that helped most.",
        "save_as": "reflection_evidence",
        "required": true,
        "choices": [
          {
            "id": "parts",
            "label": "I looked at the real parts"
          },
          {
            "id": "drawing",
            "label": "I saw what the robot drew"
          },
          {
            "id": "two_tests",
            "label": "I compared two tests"
          },
          {
            "id": "still_learning",
            "label": "I am still figuring it out"
          }
        ]
      },
      {
        "id": "explorers-swan-reflect",
        "type": "saras_checkpoint",
        "title": "Reflect with Professor Swan",
        "suggested_questions": [
          "What changed between my first idea and now?",
          "What could I try next time?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). Refer to saved early clue and current results in child-friendly language. Recognize honest uncertainties and persistence, not mere completion. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "The mystery is solved, but any specific real-world result must come from reports."
      },
      {
        "id": "explorers-reflect-gate",
        "type": "completion_gate",
        "incomplete_title": "Save your discovery",
        "complete_title": "Ready for the next discovery",
        "incomplete_prompt": "Revisit your guess and choose the clue that helped you understand.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "reflection_choice",
            "condition": "answered"
          },
          {
            "field": "reflection_evidence",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "explorers-robot-grow": {
    "step_id": "explorers-robot-grow",
    "title": "Our Robot Art Gallery",
    "subtitle": "Share your art—and the experiment behind it!",
    "phase": "Grow",
    "story": "Share your art—and the experiment behind it!",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Our Robot Art Gallery",
      "say": "Give your robot a name and choose an artwork or honest result to show.",
      "do_now": "Give your robot a name and choose an artwork or honest result to show.",
      "help_prompt": "Professor Swan, help me with this exact Robot Artist Explorers activity. Ask one short question or give one clear hint. Help me investigate my kit using its age-appropriate steps. Do not guess what happened in my experiment.",
      "transition_summary": "You saved a real clue, prediction, or observation for your investigation.",
      "next_preview": "You finished the Robot Artist mystery. Choose another adventure when ready.",
      "materials": [
        "STEM Discovery Lab Doodle Robot project (6+)",
        "Drawing paper on a flat protected surface",
        "The actual kit instruction booklet",
        "Follow the kit directions with power off during adjustments"
      ],
      "safety": [
        "Power off and remove the battery before adjusting hardware. Stop and seek help for damaged, hot or unsafe parts.",
        "Keep hands and loose items away from the moving crank when powered. Stop if parts loosen, heat up, or move unsafely."
      ],
      "parent_note": "The child leads age-appropriate building and investigation. Offer help for a specific hazard or when the child asks for physical assistance; do not supply their predictions or observations."
    },
    "blocks": [
      {
        "id": "explorers-gallery-image",
        "type": "image_gallery",
        "title": "A little inspiration",
        "prompt": "This example comes from the older Robot Artist Journey. Your own drawing is your special result.",
        "images": [
          {
            "id": "first",
            "label": "Robot drawing example",
            "path": "drawing-robot/step-7/maiden_voyage.png",
            "alt": "Drawing robot making looping marks on paper."
          }
        ]
      },
      {
        "id": "explorers-name",
        "type": "evidence_response",
        "title": "Name your robot",
        "prompt": "Choose a short name. You can write it or say it aloud.",
        "save_as": "robot_name",
        "placeholder": "My robot is called…",
        "min_length": 1,
        "required": true
      },
      {
        "id": "explorers-gallery-pick",
        "type": "choice_cards",
        "title": "What will you show?",
        "prompt": "Pick your favorite real result.",
        "save_as": "gallery_choice",
        "required": true,
        "choices": [
          {
            "id": "drawing1",
            "label": "My first drawing"
          },
          {
            "id": "drawing2",
            "label": "My second drawing"
          },
          {
            "id": "attempt",
            "label": "My experiment, even though it did not draw yet"
          }
        ]
      },
      {
        "id": "explorers-gallery-discovery",
        "type": "choice_cards",
        "title": "What will you tell someone?",
        "prompt": "Choose what you learned.",
        "save_as": "gallery_discovery",
        "required": true,
        "choices": [
          {
            "id": "motion",
            "label": "I learned how the robot moves"
          },
          {
            "id": "test",
            "label": "I tested and noticed a change"
          },
          {
            "id": "troubleshoot",
            "label": "I learned what to investigate when it did not work"
          },
          {
            "id": "next",
            "label": "I have a new idea to try"
          }
        ]
      },
      {
        "id": "explorers-swan-grow",
        "type": "saras_checkpoint",
        "title": "Show your Robot Art Gallery",
        "suggested_questions": [
          "Can I tell you what happened to my robot?",
          "What could my next art experiment be?"
        ],
        "hidden_context": "Robot Artist EXPLORERS (6–7). Celebrate the actual process: child made a guess, conducted experiments, compared evidence, and shared a discovery. Do not praise an unobserved success; if robot failed, celebrate investigation and honest report. Offer future robot adventures without implying links or kits are available yet. Keep instructions short, ask no more than one useful question at a time, give graded hints, and let the child own predictions and actual experiment reports. Never claim to have seen the real kit, do not invent outcomes, never mark trials done. Power must be off when adjusting wires, battery holder, or small fasteners. Keep clear of moving parts.",
        "reveal_policy": "Story fully revealed. Scientific claims grounded in the real test reports."
      },
      {
        "id": "explorers-grow-gate",
        "type": "completion_gate",
        "incomplete_title": "Open your gallery",
        "complete_title": "Your Robot Art Gallery is ready!",
        "incomplete_prompt": "Name the robot, pick real gallery evidence, and share what you learned.",
        "complete_prompt": "You have saved your evidence. Choose Continue when you are ready.",
        "rules": [
          {
            "field": "robot_name",
            "condition": "min_length",
            "value": 1
          },
          {
            "field": "gallery_choice",
            "condition": "answered"
          },
          {
            "field": "gallery_discovery",
            "condition": "answered"
          }
        ]
      }
    ]
  }
};

export const getRobotArtistExplorersMissionPage = (stepId) => EXPLORERS_PAGES[String(stepId || "")] || null;
export default EXPLORERS_PAGES;
